/**
 * Custom Modifier Enum Sets for ETT Codes
 *
 * This module defines 15 different modifier enum sets that can be used
 * by echocardiography codes to specify qualitative characteristics.
 * Each code references one enum set via its `modifierEnumType` field.
 */

/**
 * Centralized registry of all modifier enum sets
 */
export const ModifierEnumSets = {
  /**
   * Set 1: Severity gradations with intermediate levels
   * Use for: Conditions with nuanced severity assessment
   */
  SEVERITY_GRADATIONS: [
    'discrètement',
    'discrètement à modérément',
    'modérément',
    'modérément à sévèrement',
    'sévèrement'
  ],


  SEVERITY_GRADATIONS_VALVE: [
    'minime',
    'discrètement',
    'discrètement à modérément',
    'modérément',
    'modérément à sévèrement',
    'sévèrement'
  ],

  /**
   * Set 1b: Severity gradations with normal option
   * Use for: Conditions that can be normal or have graded severity (e.g., GLS)
   */
  SEVERITY_WITH_NORMAL: [
    'normale',
    'discrètement',
    'discrètement à modérément',
    'modérément',
    'modérément à sévèrement',
    'sévèrement'
  ],

  /**
   * Set 2: Size categories
   * Use for: Anatomical structures with size classifications
   */
  SIZE_CATEGORIES: [
    'de petit taille',
    'de taille moyenne',
    'de grande taille'
  ],

  /**
   * Set 3: Severity levels (alternative wording)
   * Use for: Conditions with discrete severity levels
   */
  SEVERITY_LEVELS: [
    'discret',
    'modéré',
    'important'
  ],

  /**
   * Set 4: Numeric ranges (as strings)
   * Use for: Parameters with predefined value ranges (e.g., LVEF ranges, age ranges)
   * Note: These are STRING ranges, not numeric values
   */
  NUMERIC_RANGES: [
    'moins de 25',
    '15-20',
    '20-25',
    '25-30',
    '30-35',
    '35-40',
    '40-45',
    '45-50',
    '50-55',
    '>55',
    '55-60',
    '60-65',
    '65-70',
    '70-75',
    '75-80'
  ],

  /**
   * Set 5: Probability levels
   * Use for: Clinical assessments with probability qualifiers
   */
  PROBABILITY_LEVELS: [
    'normal',
    'probablement normal',
    'peu élevée',
    'probablement élevée',
    'élevée',
    'ne permet pas de se prononcer'
  ],

  /**
   * Set 6: Grading levels with normality classification
   * Use for: Structured grading systems (e.g., diastolic dysfunction grading)
   */
  GRADING_LEVELS: [
    'i normal',
    'i anormal',
    'i',
    'ii',
    'iii'
  ],

  /**
   * Set 7: Duration/Time types
   * Use for: Time intervals or durations
   */
  DURATION_TYPES: [
    'normal',
    'raccourci',
    'augmenté'
  ],

  /**
   * Set 8: Function levels
   * Use for: Functional assessments (systolic, diastolic function)
   */
  FUNCTION_LEVELS: [
    'normal',
    'conservée',
    'diminué'
  ],

  /**
   * Set 9: Prosthesis types (basic)
   * Use for: Primary prosthesis classification
   */
  PROSTHESIS_TYPES: [
    'bioprothese',
    'mecanique'
  ],

  /**
   * Set 10: Prosthesis categories (detailed)
   * Use for: Specific prosthesis type identification
   */
  PROSTHESIS_CATEGORIES: [
    'bioprothese valvulaire',
    'bioprothese',
    'protheses valvulaire',
    'prothese valvulaire mecanique'
  ],

  /**
   * Set 11: Prosthesis mechanisms
   * Use for: Mechanical valve mechanism types
   */
  PROSTHESIS_MECHANISMS: [
    'a bille',
    'bicuspide (st jude)',
    'disc central'
  ],

  /**
   * Set 12: Significance levels
   * Use for: Clinical significance assessment
   */
  SIGNIFICANCE_LEVELS: [
    'non significative',
    'discret',
    'modérée',
    'sévère'
  ],

  /**
   * Set 13: Location types (valvular)
   * Use for: Multi-valve involvement
   */
  LOCATION_TYPES: [
    'mitral',
    'tricuspidien',
    'mitral et tricuspidien'
  ],

  /**
   * Set 14: Rhythm types
   * Use for: Cardiac rhythm classification
   */
  RHYTHM_TYPES: [
    'rythme sinusal',
    'tachycardie',
    'fa',
    'flutter',
    'electro-entrainé'
  ],
  
  PISA_GRADING: [
    'I',
    'I II',
    'II',
    'II a III',
    'III',
    'III a IV',
    'IV'
  ],


  IT_GRADIENT: [
    '<36',
    '37-50',
    '>50',
    '51-60',
    '>60'
  ],
  /**
   * Set 15: Heart rate types
   * Use for: Heart rate classification
   */
  HEART_RATE_TYPES: [
    'tachycarde',
    'normocarde',
    'bradycarde'
  ], 

  NORMALITY_OR_NOT: [
    'normal',
    'dilaté'
  ],

  NORMAL_OR_CONSERVED: [
    'normal',
    'conservé'
  ],

  /**
   * Set 16: Aortic leaflet locations
   * Use for: Specifying which aortic valve leaflet/cusp is affected
   */
  AORTIC_LEAFLETS: [
    'acoronaire',
    'coronaire gauche',
    'coronaire droit'
  ]
} as const;

/**
 * Type representing valid modifier enum set keys
 */
export type ModifierEnumSetKey = keyof typeof ModifierEnumSets;

/**
 * Type representing any valid modifier value from any enum set
 */
export type ModifierValue = typeof ModifierEnumSets[ModifierEnumSetKey][number];

/**
 * Helper function to get modifiers for a specific enum set
 */
export function getModifiersForEnumSet(enumSetKey: string): readonly string[] {
  if (enumSetKey in ModifierEnumSets) {
    return ModifierEnumSets[enumSetKey as ModifierEnumSetKey];
  }
  return [];
}

/**
 * Helper function to validate if a modifier belongs to a specific enum set
 */
export function isValidModifierForEnumSet(modifier: string, enumSetKey: string): boolean {
  const modifiers = getModifiersForEnumSet(enumSetKey);
  return modifiers.includes(modifier);
}

/**
 * Get all available enum set keys
 */
export function getAllEnumSetKeys(): ModifierEnumSetKey[] {
  return Object.keys(ModifierEnumSets) as ModifierEnumSetKey[];
}
